import { FC, useEffect, useMemo } from "react"
import { css } from "@emotion/react"
import { CustomerPart, OnlineChildPart } from "@ncs/ncs-api"
import { Box, Icon, Paragraph } from "@ncs/web-legos"

interface PartChildSelectorProps {
	/** Comes from the Part Details endpoint called with the parent ID. */
	parentPart: CustomerPart
	childParts: OnlineChildPart[]
	selectedChildId: string | null
	setSelectedChildId: (newId: string | null) => void
	loadingPartDetails: boolean
}

export const PartChildSelector: FC<PartChildSelectorProps> = ({
	parentPart,
	childParts,
	selectedChildId,
	setSelectedChildId,
	loadingPartDetails,
}) => {
	const variants = useMemo(() => {
		const acc: Record<string, any[]> = { Option: [] }
		childParts.forEach((part) => {
			let option = part.title
				.split(parentPart.title ?? "")
				.join("")
				.trim()
			while (
				(option.startsWith("[") && option.endsWith("]")) ||
				(option.startsWith("(") && option.endsWith(")"))
			) {
				option = option.slice(1, -1).trim()
			}
			acc.Option.push({ value: option, id: part.id })
		})
		return acc
	}, [childParts])

	const selectedChild = useMemo(() => {
		return childParts.find((part) => part.id.toString() === selectedChildId)
	}, [selectedChildId, childParts])

	useEffect(() => {
		if (!!selectedChildId && !selectedChild && !loadingPartDetails) {
			setSelectedChildId(null)
		}
	}, [selectedChildId, selectedChild, loadingPartDetails, setSelectedChildId])

	if (loadingPartDetails || (!!selectedChildId && !selectedChild)) {
		return null
	}

	if (childParts.length === 0) {
		return (
			<Paragraph small color="secondary">
				Sorry, there are no options currently available
			</Paragraph>
		)
	}

	const optionVariantSelections = (variantName: string, variantOptions: any[]) => {
		if (variantOptions?.length === 0) {
			return null
		}

		const handleVariantSelection = (id: number) => {
			setSelectedChildId(id.toString())
		}

		return (
			<div
				css={css`
					margin-bottom: 32px;
				`}
			>
				<div css={optionBoxStyle}>{variantName}</div>
				<Box display="flex" flexWrap="wrap" columnGap={1} rowGap={1} mt={1}>
					{variantOptions.map((variant) => {
						const isSelected = Number(variant.id) === Number(selectedChildId)
						return (
							<button
								key={variant.value}
								css={() => optionButtonStyle(isSelected)}
								className={isSelected ? "selected" : undefined}
								onClick={() => handleVariantSelection(variant.id)}
							>
								<Box display="flex" alignItems="center" columnGap={0.5} mb={0.5}>
									{isSelected ?
										<Icon icon="check-circle" family="solid" color="white" />
									:	<Icon
											icon="circle"
											customColor="#003264"
											css={css`
												margin-top: 6px;
											`}
										/>
									}
									<Paragraph css={() => styleVariantName(isSelected)}>
										{variant.value}
									</Paragraph>
								</Box>
							</button>
						)
					})}
				</Box>
			</div>
		)
	}

	return (
		<div>
			{Object.keys(variants ?? []).map((variantName) =>
				optionVariantSelections(variantName, variants[variantName])
			)}
		</div>
	)
}

const styleVariantName = (isSelected: boolean) => css`
	color: #ffffff;
	text-align: right;
	font-size: 16px;
	font-style: normal;
	font-weight: 500;
	line-height: normal;
	color: ${isSelected ? "white" : "#003264"};
`

const optionBoxStyle = () => css`
	color: #111827;
	font-size: 16px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
	margin-bottom: 16px;
`

const optionButtonStyle = (isSelected: boolean) => css`
	min-height: 44px;
	display: flex;
	padding: 10px 20px 2px 16px;
	align-items: center;
	gap: 12px;
	border-radius: 8px;
	border: 1px solid #003264;
	background-color: ${isSelected ? "#003264" : "white"};
`
